import ActionsPopOverItem from "@/components/ActionsPopOver/ActionsPopOverItem";
import ImpactPill from "@/components/Impact/ImpactPill";
import CrossReference from "@/components/links/CrossReference/CrossReference";
import MoreButton from "@/components/MoreButton/MoreButton";
import Skeleton from "@/components/Skeleton/Skeleton";
import StatusTags from "@/components/StatusTags/StatusTags";
import useTableStateUrl from "@/components/Table/hooks/useTableStateUrl";
import Table, { Column } from "@/components/Table/Table";
import TableHeader from "@/components/TableHeader/TableHeader";
import YearSelector from "@/components/YearSelector/YearSelector";
import { useDashboardYear } from "@/pages/dashboard/hooks/useDateRange";
import DeleteCommuteAction from "@/pages/employee/commute/DeleteCommuteAction";
import ExportCommuteAction from "@/pages/employee/commute/ExportCommuteAction";
import { Commute, useGetCommute, useGetCommuteYears } from "@/pages/employee/commute/useCommute";
import { DeleteOutlined, ExportOutlined } from "@ant-design/icons";
import { Button, Col, Row, Space } from "antd";
import React from "react";

const columns: Column<Commute>[] = [
  {
    key: "external_id",
    label: "Employee ID",
    minWidth: "182px",
    searchable: true,
    sortable: true,
  },
  {
    key: "country",
    label: "Country",
    minWidth: "168px",
    sortable: true,
    searchable: true,
  },
  {
    key: "travel_mode",
    label: "Travel mode",
    minWidth: "170px",
    searchable: true,
    sortable: true,
    renderer: (record) => (
      <CrossReference to={`/employees/travel_modes/${record.travel_mode_id}`}>
        {record.travel_mode}
      </CrossReference>
    ),
  },
  {
    key: "distance",
    label: "Total km",
    minWidth: "140px",
    sortable: true,
  },
  {
    key: "owned",
    label: "Owned or leased",
    minWidth: "220px",
    maxWidth: "220px",
    sortable: true,
    filterable: true,
    renderer: (record) => (record.owned ? "Yes" : "No"),
  },
  {
    key: "impact",
    label: "Impact",
    minWidth: "300px",
    renderer: (record) => (
      <ImpactPill
        impact={record.impact}
        type="borderless"
        incomplete={!record.dataset_id}
        incompleteTooltip={"Travel mode needs an impact reference."}
      />
    ),
  },
];

function CommuteTable() {
  const { year } = useDashboardYear();
  const [moreDropdownOpen, setMoreDropdownOpen] = React.useState(false);
  const { data: yearData, isLoading: yearsIsLoading } = useGetCommuteYears();

  const handleOpenChange = (open: boolean) => {
    setMoreDropdownOpen(open);
  };

  const { pagination, filter, search, sort, onSearchFilterSortPaginate, setPage } =
    useTableStateUrl({
      columns,
      defaultSort: { key: "external_id", order: "ascend" },
    });

  const {
    data,
    isLoading: dataIsLoading,
    isFetching: dataIsFetching,
  } = useGetCommute({
    year,
    filter,
    sort,
    search,
    pagination,
    enabled: !!year && !yearsIsLoading,
  });

  const isLoading = ((dataIsLoading || dataIsFetching) && !!year) || yearsIsLoading;

  const resetTable = () => {
    setPage(1);
  };

  return (
    <Row>
      <Col span={24}>
        <TableHeader
          left={
            <>
              <YearSelector
                optionData={yearData}
                onChange={resetTable}
                isLoading={yearsIsLoading}
              />
              {isLoading ? (
                <Skeleton style={{ width: 180, height: 32 }} />
              ) : (
                <>
                  <Space.Compact>
                    <ExportCommuteAction year={year}>
                      <Button icon={<ExportOutlined />}>Export</Button>
                    </ExportCommuteAction>
                    <MoreButton
                      onOpenChange={handleOpenChange}
                      open={moreDropdownOpen}
                      actions={
                        <DeleteCommuteAction
                          year={year}
                          onSuccess={() => setMoreDropdownOpen(false)}
                        >
                          <ActionsPopOverItem
                            danger
                            icon={<DeleteOutlined />}
                          >
                            Delete
                          </ActionsPopOverItem>
                        </DeleteCommuteAction>
                      }
                    />
                  </Space.Compact>
                </>
              )}
            </>
          }
          right={
            <>
              <StatusTags
                data={data}
                loading={isLoading}
              />
            </>
          }
        />
      </Col>
      <Col span={24}>
        <Table
          loading={isLoading}
          rowKey="id"
          data={data?.data}
          availableFilters={{
            owned: [
              { label: "Yes", value: "Yes" },
              { label: "No", value: "No" },
            ],
          }}
          onSearchFilterSortPaginate={onSearchFilterSortPaginate}
          totalRows={data?.total_count}
          pagination={pagination}
          sort={sort}
          search={search}
          filter={filter}
          columns={columns}
        />
      </Col>
    </Row>
  );
}

export default CommuteTable;
